import React from 'react';
import { Typography, Paper, Box, useTheme, useMediaQuery } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { AuditoriaDetalle } from '../../../types/consolidatedReport.types';

interface TrendLineChartProps {
  auditorias: AuditoriaDetalle[];
  timezone?: string;
}

const scoreColor = (score: number) => {
  if (score >= 80) return '#43A047';
  if (score >= 60) return '#FFA000';
  return '#E53935';
};

const TrendLineChart: React.FC<TrendLineChartProps> = ({ auditorias, timezone: _timezone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const data = [...auditorias]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((a) => {
      const d = new Date(a.date);
      const day = String(d.getUTCDate()).padStart(2, '0');
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const formLabel =
        isMobile && a.form.length > 14 ? a.form.substring(0, 14) + '…' : a.form;
      return {
        label: `${day}/${month} \u2014 ${formLabel}`,
        labelFull: `${day}/${month} \u2014 ${a.form}`,
        score: Math.round(a.score * 10) / 10,
        auditorId: a.id,
      };
    });

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const color = scoreColor(payload.score);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={isMobile ? 5 : 7}
        fill={color}
        stroke="#ffffff"
        strokeWidth={2}
      />
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const color = scoreColor(d.score);
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          boxShadow: 3,
          maxWidth: 240,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {d.labelFull}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>
          {d.score.toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={2}
      sx={{ p: isMobile ? 2 : 3, width: '100%', overflow: 'hidden' }}
    >
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        Tendencia por Auditoría
      </Typography>

      <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
        <LineChart
          data={data}
          margin={{
            left: isMobile ? -10 : 0,
            right: isMobile ? 10 : 24,
            top: 10,
            bottom: isMobile ? 50 : 65,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: isMobile ? 9 : 11 }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={80}
            stroke="#43A047"
            strokeDasharray="5 4"
            label={{
              value: '80%',
              position: 'insideTopRight',
              fontSize: 10,
              fill: '#43A047',
            }}
          />
          <ReferenceLine
            y={60}
            stroke="#FFA000"
            strokeDasharray="5 4"
            label={{
              value: '60%',
              position: 'insideTopRight',
              fontSize: 10,
              fill: '#FFA000',
            }}
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke="#667eea"
            strokeWidth={isMobile ? 2 : 3}
            dot={<CustomDot />}
            activeDot={{ r: isMobile ? 6 : 8 }}
            name="Puntaje"
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default TrendLineChart;
